import type { DatabaseSchool, DatabaseCoach, OutreachEntry } from './database-types';

export type { OutreachEntry };

export interface SchoolCoach {
  id: string;
  first_name: string;
  last_name: string;
  position: string;
  email?: string;
  phone?: string;
}

export interface BrowseSchool extends Omit<DatabaseSchool, 'created_at' | 'updated_at'> {
  coaches: SchoolCoach[];
}

export interface SavedSchool extends BrowseSchool {
  notes?: string;
  addedDate?: string;
  favorite: boolean;
  academic_status: number;
  recruitment_status: number;
  outreach_entries?: OutreachEntry[];
}

export const RECRUITMENT_STEPS: string[] = ['Outreach', 'Pre-read', 'Recruited', 'Committed'];
export const ACADEMIC_STEPS: string[] = ['Applied', 'Admitted', 'Enrolled'];

export type AcademicStage = -1 | 0 | 1 | 2;
export type RecruitmentStage = AcademicStage | 3;
export type ProgressPath = 'academic' | 'recruitment';

export interface RowerRecruitmentProgram {
  id: string;
  name: string;
  /** Compact single-line address (search / fallback). */
  location: string;
  /** Street line only; shown under the program name. */
  addressStreet: string | null;
  /** "City, ST ZIP" line. */
  addressLocality: string | null;
  team: string;
  academic: AcademicStage;
  recruitment: RecruitmentStage;
  favorite: boolean;
  notes: string;
  /** Program catalog write-up; omit UI when empty. */
  description?: string | null;
  coaches: SchoolCoach[];
}

export function stageLabel(path: ProgressPath, stage: number): string {
  return (path === 'academic' ? ACADEMIC_STEPS : RECRUITMENT_STEPS)[stage] || 'Not started';
}

export function validStage(path: ProgressPath, value: unknown): value is RecruitmentStage {
  return (
    Number.isInteger(value) && Number(value) >= -1 && Number(value) <= (path === 'academic' ? 2 : 3)
  );
}

const OUTREACH_DATE = /^\d{4}-\d{2}-\d{2}$/;
const OUTREACH_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isOutreachEntry(value: unknown): value is OutreachEntry {
  if (!value || typeof value !== 'object') return false;
  const entry = value as OutreachEntry;
  return (
    typeof entry.id === 'string' &&
    OUTREACH_ID.test(entry.id) &&
    typeof entry.date === 'string' &&
    OUTREACH_DATE.test(entry.date) &&
    typeof entry.note === 'string' &&
    entry.note.length <= 1000
  );
}

export function readOutreachEntries(value: unknown): OutreachEntry[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isOutreachEntry).map((entry) => ({
    id: entry.id,
    date: entry.date,
    note: entry.note,
  }));
}

export function assertOutreachEntries(entries: OutreachEntry[]): OutreachEntry[] {
  if (!Array.isArray(entries) || entries.length > 200) {
    throw new Error('Outreach log is too long');
  }
  const latestAllowed = new Date();
  latestAllowed.setHours(0, 0, 0, 0);
  latestAllowed.setDate(latestAllowed.getDate() + 1);
  return entries.map((entry) => {
    if (!isOutreachEntry(entry)) throw new Error('Invalid outreach entry');
    const [year, month, day] = entry.date.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime()) || date > latestAllowed) {
      throw new Error('Invalid outreach date');
    }
    return { id: entry.id, date: entry.date, note: entry.note };
  });
}

export function normalizeAcademicStage(value: number | null | undefined): AcademicStage {
  if (value === -1) return -1;
  if (Number.isInteger(value) && value >= 0 && value <= 2) return value as AcademicStage;
  return 0;
}

export function normalizeRecruitmentStage(value: number | null | undefined): RecruitmentStage {
  if (value === -1) return -1;
  if (Number.isInteger(value) && value >= 0 && value <= 3) return value as RecruitmentStage;
  return 0;
}

export function toSchoolCoach(coach: DatabaseCoach): SchoolCoach {
  return {
    id: coach.id,
    first_name: coach.first_name,
    last_name: coach.last_name,
    position: coach.position,
    email: coach.email,
    phone: coach.phone,
  };
}
